import moment from "moment";

const LINE_DATE_RE = /-\s*(\d{1,2}\/\d{1,2}\/\d{4})\s*$/;

function isPresentDate(value: unknown): value is string {
  return (
    value !== false &&
    value !== null &&
    value !== undefined &&
    value !== ""
  );
}

/** Parse trailing date from ERP line label, e.g. `… - 05/16/2026`. */
export function parseDateFromInvoiceLineName(
  name: string | undefined
): string | null {
  if (!name) return null;
  const match = name.match(LINE_DATE_RE);
  return match?.[1] ?? null;
}

/** Use invoice_date when set; otherwise first date found on line items. */
export function resolveInvoiceDateRaw(invoice: {
  invoice_date?: unknown;
  invoice_line_ids?: { name?: string }[];
}): string | null {
  if (isPresentDate(invoice.invoice_date)) {
    return String(invoice.invoice_date);
  }
  for (const line of invoice.invoice_line_ids ?? []) {
    const parsed = parseDateFromInvoiceLineName(line?.name);
    if (parsed) return parsed;
  }
  return null;
}

export function formatInvoiceDateDisplay(
  invoice: Parameters<typeof resolveInvoiceDateRaw>[0],
  language: string
): string {
  const raw = resolveInvoiceDateRaw(invoice);
  if (!raw) return "—";

  const parsed = moment(
    raw,
    ["DD/MM/YYYY", "YYYY-MM-DD", moment.ISO_8601],
    true
  );
  if (parsed.isValid()) {
    return parsed
      .locale(language.startsWith("ar") ? "ar" : "en")
      .format(language.startsWith("en") ? "DD/MM/YYYY" : "YYYY/MM/DD");
  }
  return raw;
}

export function dedupeInvoiceLines<T extends { id?: number }>(lines: T[]): T[] {
  const seen = new Set<number>();
  return lines.filter((line) => {
    const id = line?.id;
    if (id == null) return true;
    if (seen.has(id)) return false;
    seen.add(id);
    return true;
  });
}

export function parseInvoiceLineLabel(name: string): {
  title: string;
  customer?: string;
  date?: string;
} {
  const date = parseDateFromInvoiceLineName(name) ?? undefined;
  let rest = name ?? "";
  if (date) rest = rest.replace(LINE_DATE_RE, "").trim();

  const parts = rest.split(/\s+-\s+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length >= 3) {
    return {
      title: parts[0],
      customer: parts.slice(1, -1).join(" - ") || parts[1],
      date,
    };
  }
  if (parts.length === 2) {
    return { title: parts[0], customer: parts[1], date };
  }
  return { title: rest || name, date };
}

export function formatMoney(value: unknown): string {
  const n = typeof value === "number" ? value : parseFloat(String(value));
  if (!Number.isFinite(n)) return "—";
  return n.toFixed(2);
}
